import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Dropdown from '../../../../components/Dropdown';
import useFetch from '../../../../hooks/useFetch';
import ProductDivisionService from '../../../../services/productDivision.service';
import ProductService from '../../../../services/product.service';
import TableBox from '../../../../components/TableBox';
import SuggestionRow from './SuggestionRow';
import { buildHeadCells } from '../../../../utils/methods';
import { VALIDATION_FIELD_MAPPING } from '../../../../utils/appConstant';

interface MapProductContentProps {
  stockistProduct: string;
  initialDivisionId?: string;
  initialProductId?: string;
  onFormChange: (data: { division: string; product: string }) => void;
  validation: { division?: string; product?: string };
}

const SUGGESTION_HEAD_CELLS = buildHeadCells(
  [
    VALIDATION_FIELD_MAPPING.name,
    VALIDATION_FIELD_MAPPING.product_division,
    VALIDATION_FIELD_MAPPING.actions,
  ],
  'productSuggestionList'
);

const MapProductContent: React.FC<MapProductContentProps> = ({
  stockistProduct,
  initialDivisionId = '',
  initialProductId = '',
  onFormChange,
  validation,
}) => {
  const { t } = useTranslation();

  const [division, setDivision] = useState(initialDivisionId);
  const [product, setProduct] = useState('');
  const [divisionSearch, setDivisionSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [pendingProductId, setPendingProductId] = useState<string | null>(initialProductId || null);

  const { data: allDivisionOptions } = useFetch({
    service: ProductDivisionService,
    func: 'lookup',
    payload: { searchText: '' },
    dependency: [],
  });

  const { data: divisionOptions } = useFetch({
    service: ProductDivisionService,
    func: 'lookup',
    payload: { searchText: divisionSearch },
    dependency: [divisionSearch],
    skip: !divisionSearch,
  });

  const { data: allProductOptions } = useFetch({
    service: ProductService,
    func: 'lookup',
    payload: { divisionId: division },
    dependency: [division],
    skip: !division,
  });

  const { data: productOptions } = useFetch({
    service: ProductService,
    func: 'lookup',
    payload: { searchText: productSearch, divisionId: division },
    dependency: [productSearch, division],
    skip: !division || !productSearch,
  });

  const { data: suggestions, loading: suggestionsLoading } = useFetch({
    service: ProductService,
    func: 'suggestions',
    payload: { name: stockistProduct },
    dependency: [stockistProduct],
    skip: !stockistProduct,
  });

  useEffect(() => {
    if (pendingProductId && allProductOptions?.length > 0) {
      setProduct(pendingProductId);
      onFormChange({ division, product: pendingProductId });
      setPendingProductId(null);
    }
  }, [allProductOptions, pendingProductId, division, onFormChange]);

  const handleDivisionChange = useCallback((e: any) => {
    const value = e.target.value;
    setDivision(value);
    setProduct('');
    onFormChange({ division: value, product: '' });
  }, [onFormChange]);

  const handleProductChange = useCallback((e: any) => {
    const value = e.target.value;
    setProduct(value);
    onFormChange({ division, product: value });
  }, [division, onFormChange]);

  const handleSuggestionSelect = useCallback((divisionId: string, productId: string) => {
    setDivision(divisionId);
    setProduct('');
    setPendingProductId(productId);
    onFormChange({ division: divisionId, product: '' });
  }, [onFormChange]);

  const toOptions = (data: any[]) =>
    (data || []).map((o: any) => ({ label: o.name, value: String(o.id) }));

  const tableValues = useMemo(() => ({
    headCells: SUGGESTION_HEAD_CELLS,
    row: SuggestionRow,
    data: suggestions || [],
    loading: suggestionsLoading,
    total: suggestions?.length || 0,
    pagination: false,
    order: 'ASC' as any,
    orderBy: 'name',
    page: 1,
    perPage: 10,
    setPage: () => {},
    setPerPage: () => {},
    setOrder: () => {},
    setOrderBy: () => {},
    noDataFoundMessage: t('monthlyStock.addEdit.pageTexts.verifyData.table.mapModal.noSuggestions'),
    rowProps: { onSelect: handleSuggestionSelect },
  }), [suggestions, suggestionsLoading, handleSuggestionSelect]);

  return (
    <div className="row g-4">
      <div className="col-12">
        <span>{t('monthlyStock.addEdit.pageTexts.verifyData.summary.stockistProduct')}: <strong>{stockistProduct || '-'}</strong></span>
      </div>

      <div className="col-md-6">
        <div className="form-floating form-floating-outline">
          <Dropdown
            id="map_division"
            name="map_division"
            label={t('monthlyStock.addEdit.pageTexts.verifyData.table.mapModal.division')}
            placeholder={t('monthlyStock.addEdit.pageTexts.verifyData.table.mapModal.divisionPlaceholder')}
            values={division ? [division] : []}
            options={toOptions(divisionSearch ? divisionOptions : allDivisionOptions)}
            allOptions={toOptions(allDivisionOptions)}
            searchText={divisionSearch}
            onSearch={(e: any) => setDivisionSearch(e.target.value)}
            onChange={handleDivisionChange}
            error={!!validation.division}
            errorMessage={validation.division}
            required
          />
        </div>
      </div>

      <div className="col-md-6">
        <div className="form-floating form-floating-outline">
          <Dropdown
            id="map_product"
            name="map_product"
            label={t('monthlyStock.addEdit.pageTexts.verifyData.table.mapModal.product')}
            placeholder={t('monthlyStock.addEdit.pageTexts.verifyData.table.mapModal.productPlaceholder')}
            values={product ? [product] : []}
            options={toOptions(productSearch ? productOptions : allProductOptions)}
            allOptions={toOptions(allProductOptions)}
            searchText={productSearch}
            onSearch={(e: any) => setProductSearch(e.target.value)}
            onChange={handleProductChange}
            error={!!validation.product}
            errorMessage={validation.product}
            required
            disabled={!division}
          />
        </div>
      </div>

      {stockistProduct && (
        <div className="col-12">
          <p className="text-muted mb-2 small">{t('monthlyStock.addEdit.pageTexts.verifyData.table.mapModal.suggestions')}</p>
          <div style={{ maxHeight: '210px', overflowY: 'auto' }}>
            <TableBox tableValues={tableValues} />
          </div>
        </div>
      )}
    </div>
  );
};

export default MapProductContent;
